import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { driverKeys } from '@/features/drivers';
import { boxKeys } from '@/features/packing';
import { shippingApi } from './shipping.api';
import { shipmentKeys } from './shipping.queries';
import type {
  CreateShipmentRequest,
  UpdateShipmentRequest,
  AssignBoxesRequest,
  ShipmentsListParams,
  ShipmentStatus
} from '../types';

export function useCreateShipmentMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('shipping');

  return useMutation({
    mutationFn: (data: CreateShipmentRequest) => shippingApi.create(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('createSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shipmentKeys.metrics() });
    }
  });
}

export function useUpdateShipmentMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('shipping');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateShipmentRequest }) =>
      shippingApi.update(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('updateSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shipmentKeys.metrics() });
      queryClient.invalidateQueries({ queryKey: driverKeys.all });
    }
  });
}

export function useDeleteShipmentMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('shipping');

  return useMutation({
    mutationFn: (id: number) => shippingApi.delete(id),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('deleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shipmentKeys.metrics() });
    }
  });
}

export function useChangeShipmentStatusMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('shipping');

  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: ShipmentStatus }) =>
      shippingApi.changeStatus(id, status),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('statusChangeSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shipmentKeys.metrics() });
    }
  });
}

export function useConfirmShipmentMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('shipping');

  return useMutation({
    mutationFn: (id: number) => shippingApi.confirm(id),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('confirmSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shipmentKeys.metrics() });
      queryClient.invalidateQueries({ queryKey: boxKeys.lists() });
      queryClient.invalidateQueries({ queryKey: driverKeys.all });
    }
  });
}

export function useDispatchShipmentMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('shipping');

  return useMutation({
    mutationFn: (id: number) => shippingApi.dispatch(id),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('dispatchSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shipmentKeys.metrics() });
      queryClient.invalidateQueries({ queryKey: boxKeys.lists() });
      queryClient.invalidateQueries({ queryKey: driverKeys.all });
    }
  });
}

export function useAssignBoxesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('shipping');

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: AssignBoxesRequest }) =>
      shippingApi.assignBoxes(id, data),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('assignSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shipmentKeys.metrics() });
      queryClient.invalidateQueries({ queryKey: boxKeys.lists() });
      queryClient.invalidateQueries({ queryKey: driverKeys.all });
    }
  });
}

export function useSaveDraftMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('shipping');

  return useMutation({
    mutationFn: (data: CreateShipmentRequest) => shippingApi.saveDraft(data),
    meta: {
      operationType: 'create',
      fallbackSuccessMessage: t('draftSaved')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentKeys.lists() });
    }
  });
}

export function useBulkDeleteShipmentsMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('shipping');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[] }
        | { all: true; filters: Partial<ShipmentsListParams> }
    ) => shippingApi.bulkDelete(params),
    meta: {
      operationType: 'delete',
      fallbackSuccessMessage: t('bulkDeleteSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shipmentKeys.metrics() });
    }
  });
}

export function useBulkChangeStatusMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('shipping');

  return useMutation({
    mutationFn: (
      params:
        | { ids: number[]; status: ShipmentStatus }
        | {
            all: true;
            filters: Partial<ShipmentsListParams>;
            status: ShipmentStatus;
          }
    ) => shippingApi.bulkChangeStatus(params),
    meta: {
      operationType: 'update',
      fallbackSuccessMessage: t('bulkStatusChangeSuccess')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: shipmentKeys.lists() });
      queryClient.invalidateQueries({ queryKey: shipmentKeys.metrics() });
    }
  });
}
